/**
 * Reporting types + helpers shared across the DIFOT / Failed deliveries /
 * Driver performance pages. All data comes from the Worker's /api/reports/*
 * endpoints, which proxy the rpt_* Postgres functions.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from './api';

// ── DIFOT category colours (semantic, shared by tiles + charts) ────────────
export const CAT_COLORS = {
  on_time: '#1f9d57', // green
  early: '#2f6fed', // blue
  late: '#e4002b', // Amart red
  no_eta: '#6b7280', // grey
  failed: '#8a1020', // dark red
  cancelled: '#c2c7d0', // light grey
} as const;

// ── Response shapes ─────────────────────────────────────────────────────────
export interface DifotSummary {
  stops: number;
  completed: number;
  failed: number;
  cancelled: number;
  on_time: number;
  early: number;
  late: number;
  no_eta: number;
  difot_pct: number | null;
  on_time_or_early_pct: number | null;
  late_pct: number | null;
  failed_cancelled_pct: number | null;
  completed_pct: number | null;
  failed_pct: number | null;
  cancelled_pct: number | null;
  prev_difot_pct: number | null;
  difot_delta_pp: number | null;
}

export interface ArrivalWindow {
  early: number;
  on_time: number;
  late: number;
  no_eta: number;
  early_pct: number | null;
  on_time_pct: number | null;
  late_pct: number | null;
  no_eta_pct: number | null;
}

export interface TrendDay {
  day: string;
  on_time: number;
  early: number;
  late: number;
  no_eta: number;
  failed: number;
  cancelled: number;
  total: number;
}

export interface DifotResponse {
  summary: DifotSummary;
  arrival_window: ArrivalWindow;
  trend: TrendDay[];
}

export interface StopRow {
  stop_id: number;
  trip_date: string | null;
  status: string | null;
  driver_name: string | null;
  customer_name: string | null;
  region: string | null;
  address: string | null;
  arrived_time: string | null;
  completed_time: string | null;
  failed_time: string | null;
  failed_reason: string | null;
  win_start: string | null;
  win_end: string | null;
  arrival_cat: string | null;
  tz: string | null;
  total_charge: number | null;
  total_cost: number | null;
}

export interface StopsResponse {
  total: number;
  rows: StopRow[];
}

export interface FiltersResponse {
  regions: string[];
  drivers: string[];
  customers: { id: number; name: string }[];
}

export interface FailedRow {
  stop_id: number;
  trip_date: string | null;
  driver_name: string | null;
  customer_name: string | null;
  region: string | null;
  address: string | null;
  failed_time: string | null;
  failed_reason: string | null;
  failed_detail: string | null;
}

export interface FailedResponse {
  summary: { total_stops: number; failed: number; failed_pct: number | null; completed: number };
  by_reason: { reason: string; n: number }[];
  trend: { day: string; failed: number }[];
  rows: FailedRow[];
}

export interface DriverRow {
  driver_name: string;
  stops: number;
  completed: number;
  failed: number;
  on_time: number;
  early: number;
  late: number;
  no_eta: number;
  difot_pct: number | null;
  completion_pct: number | null;
  late_pct: number | null;
}

export interface DriverPerfResponse {
  rows: DriverRow[];
}

// ── Delivery cost (team_member_invoices) ─────────────────────────────────────
// Driver delivery costs from the team_member_invoices table, split into the
// three charge components: base charge, fuel levy, and surcharge ("service").
// Amounts are AUD; component amounts are ex-GST, `total` is inc-GST.
export interface DeliveryCostSummary {
  invoices: number;
  drivers: number;
  total: number;
  sub_total: number;
  total_tax: number;
  base_charge: number;
  fuel_levy: number;
  surcharge: number;
  stops: number;
  runs: number;
  cost_per_stop: number | null;
  fuel_levy_pct: number | null;
}

export interface CostComponent {
  component: string;
  amount: number;
}

export interface CostWeek {
  week_start: string; // Monday of the costing week
  base_charge: number;
  fuel_levy: number;
  surcharge: number;
  total: number;
  stops: number;
  cost_per_stop: number | null;
}

export interface CostDriverRow {
  team_member_id: number;
  invoices: number;
  total: number;
  base_charge: number;
  fuel_levy: number;
  surcharge: number;
  stops: number;
  cost_per_stop: number | null;
}

export interface DeliveryCostResponse {
  summary: DeliveryCostSummary;
  breakdown: CostComponent[];
  weekly: CostWeek[];
  by_driver: CostDriverRow[];
  statuses: string[];
}

export interface InvoiceRow {
  team_member_invoice_id: number;
  team_member_id: number;
  invoice_number: string | null;
  status: string | null;
  invoice_date: string | null;
  start_date: string | null;
  end_date: string | null;
  base_charge: number;
  fuel_levy: number;
  surcharge: number;
  sub_total: number;
  total_tax: number;
  total: number;
  stops: number | null;
  runs: number | null;
  cost_per_stop: number | null;
}

export interface InvoicesResponse {
  total: number;
  rows: InvoiceRow[];
}

// Cost-component colours, shared by the trend chart + legend.
export const COST_COLORS = {
  base_charge: '#2f6fed', // blue
  fuel_levy: '#f5a623', // amber
  surcharge: '#1f9d57', // green
} as const;

// ── Customer feedback (team_member_ratings) ──────────────────────────────────
export interface FeedbackSummary {
  avg_rating: number | null;
  ratings: number;
  completed: number;
  response_rate_pct: number | null;
  low: number;
  low_pct: number | null;
}

export interface RatingBucket {
  stars: number;
  n: number;
  pct: number | null;
}

export interface FbDriver {
  driver_name: string;
  avg_rating: number | null;
  ratings: number;
}

export interface FbAttention {
  driver_name: string;
  low: number;
  total: number;
  pct_low: number | null;
}

export interface FbRegion {
  region: string;
  avg_rating: number | null;
  ratings: number;
}

export interface FeedbackResponse {
  summary: FeedbackSummary;
  distribution: RatingBucket[];
  driver_leaderboard: FbDriver[];
  driver_attention: FbAttention[];
  region_leaderboard: FbRegion[];
}

export interface FeedbackStopRow {
  stop_id: number;
  rating: number;
  feedback: string | null;
  driver_name: string | null;
  region: string | null;
  completed_time: string | null;
  source_reference: string | null;
  tracking_link: string | null;
  customer_name: string | null;
  tz: string | null;
}

export interface FeedbackStopsResponse {
  total: number;
  rows: FeedbackStopRow[];
}

// ── Filters + query string ───────────────────────────────────────────────────
export interface Filters {
  from: string;
  to: string;
  region: string;
  customer: string;
  driver: string;
}

export function defaultFilters(): Filters {
  const { from, to } = presetRange('yesterday');
  return { from, to, region: '', customer: '', driver: '' };
}

// ── Date range presets ───────────────────────────────────────────────────────
// Quick ranges shared by every report's filter bar. All windows END on
// yesterday — today is excluded because the current day's data is still
// incomplete, and defaulting to a closed day keeps the initial load small.
export type DatePresetKey = 'yesterday' | 'last_week' | 'last_month';

export const DATE_PRESETS: { key: DatePresetKey; label: string }[] = [
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_week', label: 'Last week' },
  { key: 'last_month', label: 'Last month' },
];

/** Resolve a preset to a concrete {from, to} range (inclusive, ending yesterday). */
export function presetRange(key: DatePresetKey): { from: string; to: string } {
  const yesterday = addDays(isoToday(), -1);
  switch (key) {
    case 'yesterday':
      return { from: yesterday, to: yesterday };
    case 'last_week':
      return { from: addDays(yesterday, -6), to: yesterday }; // 7 days incl. yesterday
    case 'last_month':
      return { from: addDays(yesterday, -29), to: yesterday }; // 30 days incl. yesterday
  }
}

/** Which preset (if any) the current range matches — used to highlight the chip. */
export function activePreset(from: string, to: string): DatePresetKey | null {
  return DATE_PRESETS.find((p) => {
    const r = presetRange(p.key);
    return r.from === from && r.to === to;
  })?.key ?? null;
}

// ── Cost-report date presets (distinct from the rolling presets above) ───────
// Delivery cost is billed per costing week, which runs Monday–Sunday. These
// presets are calendar-aligned (whole weeks / whole month) rather than rolling
// windows, so the report lines up with how invoices are issued.
export type CostPresetKey = 'this_week' | 'last_week' | 'last_month';

export const COST_DATE_PRESETS: { key: CostPresetKey; label: string }[] = [
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'last_month', label: 'Last month' },
];

/** Monday (UTC) of the costing week containing `iso`. */
function mondayOf(iso: string): string {
  const dow = new Date(`${iso}T00:00:00Z`).getUTCDay(); // 0=Sun … 6=Sat
  return addDays(iso, -((dow + 6) % 7));
}

/** Resolve a cost preset to a Mon–Sun week or whole-month range. */
export function costPresetRange(key: CostPresetKey): { from: string; to: string } {
  const thisMonday = mondayOf(isoToday());
  switch (key) {
    case 'this_week':
      return { from: thisMonday, to: addDays(thisMonday, 6) };
    case 'last_week': {
      const lastMonday = addDays(thisMonday, -7);
      return { from: lastMonday, to: addDays(lastMonday, 6) };
    }
    case 'last_month': {
      const now = new Date();
      const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
      const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
      return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
    }
  }
}

export function activeCostPreset(from: string, to: string): CostPresetKey | null {
  return COST_DATE_PRESETS.find((p) => {
    const r = costPresetRange(p.key);
    return r.from === from && r.to === to;
  })?.key ?? null;
}

export function filtersToQuery(f: Filters): string {
  const p = new URLSearchParams();
  if (f.from) p.set('from', f.from);
  if (f.to) p.set('to', f.to);
  if (f.region) p.set('region', f.region);
  if (f.customer) p.set('customer', f.customer);
  if (f.driver) p.set('driver', f.driver);
  return p.toString();
}

// ── Data fetching hook ────────────────────────────────────────────────────────
export function useApi<T>(path: string | null): {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!path) return;
    let active = true;
    setLoading(true);
    setError(null);
    apiFetch<T>(path)
      .then((d) => {
        if (active) setData(d);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load report');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [path, nonce]);

  return { data, error, loading, reload };
}

// ── Date helpers (UTC ISO, no external dep) ──────────────────────────────────
export function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

export function addDays(iso: string, delta: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

// ── Formatting ────────────────────────────────────────────────────────────────
const intFmt = new Intl.NumberFormat('en-AU');
const moneyFmt = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

export function num(n: number | null | undefined): string {
  return n == null ? '0' : intFmt.format(n);
}

export function pct(n: number | null | undefined, digits = 1): string {
  return n == null ? '—' : `${n.toFixed(digits)}%`;
}

export function money(n: number | null | undefined): string {
  return n == null ? '—' : moneyFmt.format(n);
}

const moneyCompactFmt = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** Compact currency for large headline figures, e.g. $1.8M. */
export function moneyCompact(n: number | null | undefined): string {
  return n == null ? '—' : moneyCompactFmt.format(n);
}

/** Render a timestamp in a stop's local timezone (falls back to local). */
export function localTime(iso: string | null, tz: string | null): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-AU', {
      day: '2-digit',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: tz || undefined,
    }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleString();
  }
}

// ── CSV export ────────────────────────────────────────────────────────────────
export function downloadCsv(filename: string, columns: string[], rows: (string | number | null)[][]): void {
  const escape = (v: string | number | null): string => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [columns.map(escape).join(','), ...rows.map((r) => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** A stable filter state hook used by every report page. */
export function useFilters(): [Filters, (patch: Partial<Filters>) => void] {
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const patch = useCallback((p: Partial<Filters>) => setFilters((f) => ({ ...f, ...p })), []);
  return [filters, patch];
}

/** Memoised query string for the current filters. */
export function useQuery(filters: Filters): string {
  return useMemo(() => filtersToQuery(filters), [filters]);
}
